/**
 * Product vocabulary for staff surfaces (GDN-3226, PRD §3 and AC-3).
 *
 * The words staff read are Missing, Requested, Received, Waived and Remind.
 * Only four of those are storage states. "Requested" is DERIVED from delivery
 * state and is never a completion status — a delivered message says nothing
 * about whether the patient acted, so it must never be mistaken for Received.
 */

import type {
  DeliveryHistoryResponse,
  DeliveryTouchpoint,
  RequirementRow, SubjectLanguage } from './types';

/** What a staff-facing row reads as. */
export type DisplayState = 'missing' | 'requested' | 'received' | 'waived' | 'cancelled';

/**
 * Map a row to its staff-facing state.
 *
 * `display_state` from the backend carries the settled states; "Requested" is
 * layered on top of Missing when `has_active_session` is true.
 *
 * What that flag MEANS is the backend's to define and has already moved twice
 * — first "a session exists", then "an attempt was accepted" (GDN-3356), and
 * GDN-3363 tightens it again to "delivered and not terminally failed". This
 * function deliberately does not restate the current definition: a comment that
 * pins a moving definition is wrong after the next tightening, and the caller
 * passes the flag through unchanged precisely so the display follows the
 * backend without a frontend change.
 */
export const resolveDisplayState = (
  // `display_state` is optional because a caller can legitimately not have one:
  // the patients-list filter block (GDN-3423) carries the storage `state` only.
  // Optional says that; passing an empty string would claim the backend sent a
  // blank presentation string, which is a different and untrue thing.
  row: Pick<RequirementRow, 'state'> & Partial<Pick<RequirementRow, 'display_state'>>,
  hasActiveSession: boolean,
): DisplayState => {
  // Prefer the storage state: display_state is a presentation string, and a
  // backend that adds a value we do not know about should still land somewhere
  // sensible rather than silently reading as Missing.
  switch (row.state) {
    case 'satisfied':
      return 'received';
    case 'waived':
      return 'waived';
    case 'cancelled':
      return 'cancelled';
    case 'open':
      return hasActiveSession ? 'requested' : 'missing';
    default:
      return row.display_state?.toLowerCase() === 'received' ? 'received' : 'missing';
  }
};

export const DISPLAY_STATE_LABELS: Record<DisplayState, string> = {
  missing: 'Missing',
  requested: 'Requested',
  received: 'Received',
  waived: 'Waived',
  cancelled: 'Cancelled',
};

/** A row still owes the practice something. Drives the missing-item count (AC-3). */
export const isOutstanding = (state: DisplayState): boolean =>
  state === 'missing' || state === 'requested';

/**
 * AC-4: the action reads "Send request" until a request exists, then "Remind".
 * Keyed off the same backend-owned flag as the Requested state, so the label
 * and the chip can never disagree about whether this row has been chased.
 */
export const sendActionLabel = (hasActiveSession: boolean): 'Send request' | 'Remind' =>
  hasActiveSession ? 'Remind' : 'Send request';

/**
 * When a touchpoint reached the patient, for ordering and for the "requested by
 * SMS 4 days ago" line. Touchpoints carry four timestamps and any of them can be
 * null; prefer the one that means "the patient could have seen it".
 *
 * Deliberately NOT the attempts array: an attempt records an outbox try
 * (channel, sequence, status) and carries no send time, only `scheduled_at`.
 */
export const touchpointTimestamp = (
  touchpoint: DeliveryTouchpoint,
): string | undefined =>
  touchpoint.delivered_at ?? touchpoint.sent_at ?? touchpoint.queued_at ?? undefined;

/**
 * Most recent touchpoint, newest first — the derived half of AC-3. Returns
 * undefined when nothing has gone out, which is what makes a row read Missing
 * rather than Requested.
 */
export const latestTouchpoint = (
  history: DeliveryHistoryResponse | undefined,
): DeliveryTouchpoint | undefined => {
  const touchpoints = history?.touchpoints ?? [];
  if (touchpoints.length === 0) return undefined;
  return [...touchpoints].sort((a, b) =>
    (touchpointTimestamp(b) ?? '').localeCompare(touchpointTimestamp(a) ?? ''),
  )[0];
};

/**
 * The date on a Requested line, e.g. "Aug 30".
 *
 * One helper for both staff surfaces. They previously formatted the SAME field
 * two different ways — the record section relative ("4 days ago"), the missing
 * list absolute ("8/30/2026") — so one requirement read differently depending
 * on which screen you opened. Absolute short form matches the wording the PM
 * ruled for the bounced-request line (GDN-3363), so the two lines will read
 * alike when that lands.
 *
 * The year appears only when it is not the current one: "Aug 30" is unambiguous
 * for the recent requests a chase list is made of, and misleading for an old one.
 */
export const formatRequestDate = (iso: string | null | undefined): string | undefined => {
  if (!iso) return undefined;
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return undefined;
  const sameYear = parsed.getFullYear() === new Date().getFullYear();
  return parsed.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
  });
};

/**
 * The "Requested by SMS Aug 30" line, shared by both staff surfaces so they
 * cannot drift apart again. Channel and date are each optional: a row can be
 * Requested with neither, and the line degrades to plain "Requested".
 */
export const requestedDetail = (
  channel: string | null | undefined,
  iso: string | null | undefined,
): string => {
  const when = formatRequestDate(iso);
  return ['Requested', channel ? `by ${channel.toUpperCase()}` : undefined, when]
    .filter(Boolean)
    .join(' ');
};
/**
 * What became of the last request (#4409).
 *
 * Derived from the row rather than imported by name. The generated module
 * carries FOUR structurally identical copies of this shape —
 * `LastRequestOutcomeResponse` through `...Response4`, one per row DTO that
 * embeds it — because the codegen de-duplicates by name and suffixes the
 * collisions. Those numbers come from declaration order in the spec, so
 * naming one directly would silently re-point at a different DTO's copy the
 * next time an endpoint is added above it. Reading the field off the row the
 * code actually consumes cannot drift that way.
 */
export type LastRequestOutcome = NonNullable<RequirementRow['last_request_outcome']>;

/**
 * Per-cause tails, exactly as the PM ruled them on GDN-3363.
 *
 * Deliberately no `default` fallback: a reason this map does not know produces
 * NO line rather than a guessed one. A wrong remedy on a chase list is worse
 * than a missing one — "check contact info" against a carrier outage sends
 * staff to edit a phone number that was always fine.
 */
const OUTCOME_TAILS: Record<string, string> = {
  invalid_address: 'check contact info',
  unreachable: 'carrier could not deliver',
  failed_other: 'delivery failed',
};

const channelWord = (channel: string | null | undefined): string | undefined => {
  if (!channel) return undefined;
  return channel.toLowerCase() === 'sms' ? 'SMS' : 'Email';
};

/**
 * The bounced-request line (AC-22), shared by both staff surfaces.
 *
 * Exists because of what the tightened requested-flag does: once a bounce stops
 * counting as requested, a requirement that WAS chased and never arrived reads
 * on the row exactly like one nobody has touched. Same state, same action, same
 * blank detail. This line is the only thing that tells them apart.
 *
 * **Rendered only for `status === 'failed'`.** The outcome is present on
 * delivered requests too, so keying off its presence would print "not
 * delivered" under a message that arrived.
 *
 * Note what this does NOT do: rank anything. The PM's priority for a
 * multi-channel failure (unsubscribed > invalid_address > unreachable >
 * failed_other) is applied by the backend, which is why this field is one
 * outcome and not a list. Re-ranking here would be a second opinion about a
 * decision already made, on data that cannot express it.
 */
export const outcomeDetail = (
  outcome: LastRequestOutcome | null | undefined,
  /**
   * Required, not optional with a default. The PM's copy names the subject
   * twice, and an organization that calls them clients must not read
   * "patient" here while every other line on the same row says client. A
   * default would let the next surface omit it and reintroduce that silently.
   */
  subject: SubjectLanguage,
): string | undefined => {
  if (!outcome || outcome.status !== 'failed') return undefined;

  const when = formatRequestDate(outcome.at);
  const channel = channelWord(outcome.channel);

  // An opt-out is not a failed delivery, and staff must not read it as one:
  // the remedy for every other cause is "fix it and re-send", and here
  // re-sending is the thing that must not happen.
  if (outcome.reason === 'unsubscribed') {
    const head = channel
      ? `${subject.singularCap} opted out of ${channel}`
      : `${subject.singularCap} opted out`;
    return when ? `${head} (${when}).` : `${head}.`;
  }

  /*
   * Nothing was sent at all, so the not-delivered frame is dropped: it would
   * describe an attempt that never happened. Wording is the PM's, from AC-22.
   *
   * It reads differently from the opted-out line on purpose, because the staff
   * action differs — opted out means the patient said no and staff respect it;
   * no eligible channel means staff have no way to reach them and the fix is
   * contact info. It also sits outside the cause priority, since it cannot
   * co-occur with an attempted channel's failure: if any channel was tried,
   * that channel's cause wins.
   */
  if (outcome.reason === 'no_eligible_channel') {
    const head = when ? `No request sent (${when})` : 'No request sent';
    return `${head} — no way to reach this ${subject.singular}. Check contact info.`;
  }

  const tail = outcome.reason ? OUTCOME_TAILS[outcome.reason] : undefined;
  if (!tail) return undefined;

  // "(SMS, Aug 30)", "(SMS)", "(Aug 30)", or nothing — each part is optional
  // because either can be absent on the DTO, and a literal "(null, )" on a
  // chase list is worse than a shorter true sentence.
  const context = [channel, when].filter(Boolean).join(', ');
  const head = context
    ? `Last request not delivered (${context})`
    : 'Last request not delivered';

  return `${head} — ${tail}.`;
};

/** What staff read for a delivery touchpoint's status (GDN-3398). */
export type DeliveryTone = 'pending' | 'good' | 'bad';

/**
 * Product words for a touchpoint status, and the tone that carries them.
 *
 * The backend's vocabulary is the vendor's: queued, sent, delivered, failed,
 * bounced, dropped, opened, clicked. Staff should not have to know which of
 * those mean the message arrived.
 *
 * An unrecognised status keeps its raw value rather than falling into a
 * reassuring default. A new vendor state rendering as "Sent" would be a quiet
 * lie; rendering as itself is merely ugly, and ugly is visible.
 */
const DELIVERY_STATUS: Record<string, { label: string; tone: DeliveryTone }> = {
  queued: { label: 'Queued', tone: 'pending' },
  sent: { label: 'Sent', tone: 'pending' },
  delivered: { label: 'Delivered', tone: 'good' },
  opened: { label: 'Opened', tone: 'good' },
  clicked: { label: 'Link opened', tone: 'good' },
  failed: { label: 'Not delivered', tone: 'bad' },
  dropped: { label: 'Not delivered', tone: 'bad' },
  bounced: { label: 'Bounced', tone: 'bad' },
};

export const deliveryStatusLabel = (status: string): string =>
  DELIVERY_STATUS[status?.toLowerCase()]?.label ?? status;

export const deliveryStatusTone = (status: string): DeliveryTone =>
  DELIVERY_STATUS[status?.toLowerCase()]?.tone ?? 'pending';
